import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect
} from "react";
import api from "../services/api";
import { AxiosError } from "axios";
import secureLocalStorage from "react-secure-storage";

interface IUser {
  id: string;
  people_id: string;
  email: string;
  avatar: string;
  is_teacher: boolean;
  is_admin: boolean;
  avatar_url: string;
  people: {
    id: string;
    address_id: string;
    contact_id: string;
    first_name: string;
    last_name: string;
    born_at: string;
    gender: string;
    cpf: string;
    is_finished: true;
    created_at: string;
    updated_at: string;
  };
  address: {
    id: string;
    zipcode: string;
    street: string;
    number: number;
    district: string;
    city: string;
    state: string;
    country: string;
    is_finished: boolean;
    created_at: string;
    updated_at: string;
  };
  contact: {
    id: string;
    phone: string;
    is_finished: boolean;
    created_at: string;
    updated_at: string;
  };
}

interface IData {
  user: {
    id: string;
    people_id: string;
    email: string;
    avatar: "avatar_default.png" | string;
    is_admin: boolean;
    is_blocked: boolean;
    created_at: string;
    updated_at: string;
    avatar_url: string;
  };
  people: {
    id: string;
    address_id: string;
    contact_id: string;
    first_name: string;
    last_name: string;
    born_at: string;
    gender: string;
    document: string;
    is_finished: boolean;
    created_at: string;
    updated_at: string;
  };
  address: {
    id: string;
    zipcode: string;
    street: string;
    number: number;
    district: string;
    city: string;
    state: string;
    country: string;
    is_finished: boolean;
    created_at: string;
    updated_at: string;
  };
  contact: {
    id: string;
    phone: string;
    is_finished: boolean;
    created_at: string;
    updated_at: string;
  };
  token: string;
  is_block_editor: boolean;
  tokenFirebase: string;
  payment_method: {
    is_active: boolean;
    customer_id?: string;
    subscription_id?: string;
  };
}

interface AuthContextData {
  data: IData;
  loading: boolean;
  typeUser: "Paid" | "Unpaid" | "Suspended" | "Loading";
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  createdAccount?: boolean;
  setCreatedAccount?: React.Dispatch<React.SetStateAction<boolean>>;
  loadingTypeUser?: boolean;
  userStripe: any;
  modifyProfile: boolean;
  setModifyProfile: React.Dispatch<React.SetStateAction<boolean>>;
  permissionType: "loading" | "editor" | "normal";
  setPermissionType: React.Dispatch<
    React.SetStateAction<"loading" | "editor" | "normal">
  >;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<IData | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingTypeUser, setLoadingTypeUser] = useState(true);
  const [modifyProfile, setModifyProfile] = useState(false);
  const [permissionType, setPermissionType] = useState<
    "editor" | "normal" | "loading"
  >("loading");

  const [userStripe, setUserStripe] = useState("");

  const [typeUser, setTypeUser] = useState<
    "Paid" | "Unpaid" | "Suspended" | "Loading"
  >("Paid");
  const [createdAccount, setCreatedAccount] = useState(false);

  //removing old localstorages key

  useEffect(() => {
    secureLocalStorage.removeItem("@UXDOC:token");
    secureLocalStorage.removeItem("@UXDOC:data");
    secureLocalStorage.removeItem("@UXDOCNEW:token");
    secureLocalStorage.removeItem("@UXDOCNEW:data");
  }, []);

  useEffect(() => {
    let isRefreshing = false;
    let failedRequestQueue: Array<{
      onSuccess: (token: string) => void;
      onFailure: (err: AxiosError) => void;
    }> = [];

    api.interceptors.response.use(
      response => {
        return response;
      },
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          if (
            error?.response?.data?.message ===
            "Incorrect email/password combination."
          ) {
            // if incorrect email we throw error to get catch on sign function
            throw error;
          } else if (error.response.data?.message === "Invalid JWT token") {
            //refresh token
            const originalConfig = error.config;

            const oldToken = secureLocalStorage.getItem("@UXDOCNEW2:token");

            if (!isRefreshing) {
              isRefreshing = true;

              api
                .put("/sessions/refresh-token", { token: oldToken })
                .then(response => {
                  const { refresh_token } = response.data;
                  secureLocalStorage.setItem("@UXDOCNEW2:token", refresh_token);

                  api.defaults.headers.common[
                    "Authorization"
                  ] = `Bearer ${refresh_token}`;

                  failedRequestQueue.forEach(request =>
                    request.onSuccess(refresh_token)
                  );
                  failedRequestQueue = [];
                })
                .catch(err => {
                  failedRequestQueue.forEach(request => request.onFailure(err));
                  failedRequestQueue = [];
                })
                .finally(() => {
                  isRefreshing = false;
                });
            }

            return new Promise((resolve, reject) => {
              failedRequestQueue.push({
                onSuccess: (token: string) => {
                  originalConfig.headers["Authorization"] = `Bearer ${token}`;

                  resolve(api(originalConfig));
                },
                onFailure: (err: AxiosError) => {
                  reject(err);
                  signOut();
                }
              });
            });
          } else {
            if (
              error.response.data?.message === "access authorization denied"
            ) {
              signOut();
            } else {
              throw error;
            }
          }
        } else {
          //if there is any other kind of error throw it
          throw error;
        }
      }
    );
  }, []);

  useEffect(() => {
    const autoLoad = async () => {
      const token = secureLocalStorage.getItem("@UXDOCNEW2:token");
      const data = secureLocalStorage.getItem("@UXDOCNEW2:data");

      if (token && data) {
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        setData(JSON.parse(String(data)));
      }

      try {
        const fetchProfile = await api.get("profile");
      } catch (error) {
        console.log(error, "error when request profile");
        signOut();
      }
    };

    autoLoad();
  }, []);

  const signIn = useCallback(async (email, password) => {
    try {
      const response = await api.post("sessions", {
        email,
        password
      });

      const {
        token,
        user,
        address,
        contact,
        people,
        tokenFirebase,
        payment_method,
        is_block_editor
      } = response.data as IData;

      secureLocalStorage.setItem("@UXDOCNEW2:token", token);
      secureLocalStorage.setItem(
        "@UXDOCNEW2:data",
        JSON.stringify(response.data)
      );

      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      setData({
        user,
        address,
        contact,
        people,
        token,
        tokenFirebase,
        payment_method,
        is_block_editor
      });

      if (payment_method.is_active) {
        setTypeUser("Paid");
      } else if (!payment_method.subscription_id) {
        setTypeUser("Unpaid");
      }

      if (!payment_method.is_active && payment_method.subscription_id) {
        setTypeUser("Suspended");
      }

      setLoadingTypeUser(false);
    } catch (error: any) {
      console.log(error, "error login");
      throw error;
    }
  }, []);

  const signOut = useCallback(async () => {
    secureLocalStorage.removeItem("@UXDOCNEW2:token");
    secureLocalStorage.removeItem("@UXDOCNEW2:data");

    setData(null);
  }, []);

  useEffect(() => {
    if (data) {
      setUserStripe(data?.payment_method?.subscription_id);
      if (data?.is_block_editor) {
        setPermissionType("editor");
      } else {
        setPermissionType("normal");
      }
    }
  }, [data]);

  return (
    <AuthContext.Provider
      value={{
        data,
        loading,
        typeUser,
        signIn,
        signOut,
        createdAccount,
        setCreatedAccount,
        loadingTypeUser,
        userStripe,
        modifyProfile,
        setModifyProfile,
        permissionType,
        setPermissionType
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// creating hook

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used with an AuthProvider");
  }

  return context;
}

export { AuthProvider, useAuth };
